"use client";

import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import { play } from "cuelume";
import type { Locale } from "@/lib/i18n";
import { toLocalizedPath } from "@/lib/i18n";
import { chooseRandomItem } from "@/lib/random-test";
export type RandomTestItem = {
  id: string;
  title: string;
  description: string;
};
const SPIN_DURATION_MS = 6_500;
export default function RandomTestSlotMachine({
  locale,
  tests
}: {
  locale: Locale;
  tests: RandomTestItem[];
}) {
  const [selected, setSelected] = useState(tests[0]);
  const [spinning, setSpinning] = useState(false);
  const [display, setDisplay] = useState(tests[0]);
  const [tickCount, setTickCount] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const spin = useCallback(() => {
    if (spinning || tests.length === 0) return;
    setSpinning(true);
    const winner = chooseRandomItem(tests, selected?.id);
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!winner) return;
    if (reducedMotion) {
      setDisplay(winner);
      setSelected(winner);
      setSpinning(false);
      play("success");
      return;
    }
    let index = Math.max(0, tests.findIndex(item => item.id === display?.id));
    let isPressed = false;
    const startedAt = Date.now();
    const tick = () => {
      index = (index + 1) % tests.length;
      setDisplay(tests[index]);
      setTickCount(count => count + 1);
      play(isPressed ? "release" : "press");
      isPressed = !isPressed;
      const progress = Math.min(1, (Date.now() - startedAt) / SPIN_DURATION_MS);
      if (progress < 1) {
        timerRef.current = setTimeout(tick, 45 + Math.round(progress ** 2 * 275));
        return;
      }
      setDisplay(winner);
      setSelected(winner);
      setSpinning(false);
      play("success");
    };
    timerRef.current = setTimeout(tick, 45);
  }, [display?.id, selected?.id, spinning, tests]);
  useEffect(() => {
    startRef.current = setTimeout(spin, 0);
    return () => {
      if (startRef.current) clearTimeout(startRef.current);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // Spin once when the page is entered.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!selected) return null;
  const copy = locale === "ko" ? {
    eyebrow: "랜덤 테스트 슬롯",
    title: "오늘은 어떤 테스트가 나올까요?",
    description: "슬롯머신처럼 랜덤으로 오늘의 테스트를 골라보세요.",
    spin: "다시 돌리기",
    spinning: "테스트를 고르는 중…",
    result: "오늘의 테스트",
    open: "테스트 시작하기"
  } : {
    eyebrow: "Random test slot",
    title: "Which quiz will you get today?",
    description: "Spin the slot and discover a random quiz for today.",
    spin: "Spin again",
    spinning: "Picking a quiz…",
    result: "Your random quiz",
    open: "Start this quiz"
  };
  return <section className="card mx-auto flex w-full max-w-xl flex-col items-center gap-5 text-center">
      <div>
        <p className="font-display text-xs text-brand-500">{copy.eyebrow}</p>
        <h1 className="mt-2 font-display text-2xl text-slate-900">{copy.title}</h1>
        <p className="mt-2 text-sm leading-6 text-slate-600">{copy.description}</p>
      </div>
      <p className="sr-only" role="status" aria-live="polite">
        {spinning ? copy.spinning : `${copy.result}: ${selected.title}`}
      </p>
      <div className="relative w-full overflow-hidden rounded-3xl bg-slate-900 px-5 py-8 text-white shadow-inner ring-4 ring-brand-200/50" aria-busy={spinning}>
        <span className="absolute inset-x-5 top-1/2 h-px bg-gradient-to-r from-transparent via-brand-300/60 to-transparent" aria-hidden />
        <p className="text-xs uppercase tracking-[0.2em] text-brand-200">{spinning ? copy.spinning : copy.result}</p>
        <div key={`${display?.id}-${tickCount}`} className={`mt-4 min-h-20 ${spinning ? "random-slot-tick" : "animate-pop"}`} aria-hidden={spinning}>
          <p className="font-display text-xl">{display?.title}</p>
          <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-slate-300">{display?.description}</p>
        </div>
      </div>
      <div className="flex flex-wrap justify-center gap-3">
        <button type="button" onClick={spin} disabled={spinning} className="btn-primary inline-flex min-h-11 items-center justify-center disabled:cursor-wait disabled:opacity-60">
          {copy.spin}
        </button>
        {!spinning && selected.id === display?.id ? <Link href={toLocalizedPath(`/tests/${selected.id}`, locale)} className="btn-ghost inline-flex min-h-11 items-center justify-center" prefetch={false}>
            {copy.open}
          </Link> : null}
      </div>
    </section>;
}